import { type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactECharts from "echarts-for-react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { AppButton, AppIconButton, AppTextField, AppToggleField } from "./form";
import { AppFormField, AppFormGrid } from "./layout";
import {
  AppDialog,
  AppDialogActions,
  AppDialogCancelButton,
  AppDialogContent,
  AppDialogTitle,
} from "./dialog";
import { GRID_HALF } from "../lib/ui/formLayout";

export type ResourceQueryCallRow = {
  id: string;
  executedAt?: string | null;
  origin?: string | null;
  username?: string | null;
  query?: string | null;
  success: boolean;
  error?: string | null;
  durationMs?: number | null;
};

export type ResourceQueryCallPage = {
  content: ResourceQueryCallRow[];
  page: number;
  size: number;
  totalElements: number;
};

export const QUERY_HISTORY_PAGE_SIZE = 10;
export const QUERY_HISTORY_WINDOW = 100;

export type ResourceQueryStats = {
  queryCallCount?: number;
  querySuccessCount?: number;
  queryFailureCount?: number;
  autoDisabledAt?: string | null;
};

type Props = {
  saved: boolean;
  stats: ResourceQueryStats;
  history: ResourceQueryCallRow[];
  historyPage?: number;
  historyTotal?: number;
  historyPageSize?: number;
  onHistoryPageChange?: (page: number) => void;
  historyLoading?: boolean;
  resetting?: boolean;
  onReset?: () => void;
  autoDisableEnabled: boolean;
  minSuccessPercent: string;
  minCalls: string;
  onAutoDisableChange: (patch: {
    autoDisableEnabled?: boolean;
    minSuccessPercent?: string;
    minCalls?: string;
  }) => void;
};

export function formatQueryStatsCaption(stats: ResourceQueryStats): string {
  const total = stats.queryCallCount ?? 0;
  const ok = stats.querySuccessCount ?? 0;
  const fail = stats.queryFailureCount ?? 0;
  return `${total} · ${ok}/${fail}`;
}

export default function ResourceQueryStatsPanel({
  saved,
  stats,
  history,
  historyPage = 0,
  historyTotal,
  historyPageSize = QUERY_HISTORY_PAGE_SIZE,
  onHistoryPageChange,
  historyLoading,
  resetting,
  onReset,
  autoDisableEnabled,
  minSuccessPercent,
  minCalls,
  onAutoDisableChange,
}: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [queryView, setQueryView] = useState<string | null>(null);
  const total = stats.queryCallCount ?? 0;
  const success = stats.querySuccessCount ?? 0;
  const failure = stats.queryFailureCount ?? 0;
  const rate = total > 0 ? Math.round((success * 100) / total) : null;

  const option = useMemo(() => {
    if (total <= 0) return null;
    return {
      animation: false,
      tooltip: { trigger: "item" },
      series: [
        {
          type: "pie",
          radius: ["62%", "88%"],
          avoidLabelOverlap: true,
          label: { show: false },
          data: [
            {
              name: t("resourceQueryStats.success"),
              value: success,
              itemStyle: { color: "#2e7d32" },
            },
            {
              name: t("resourceQueryStats.failure"),
              value: failure,
              itemStyle: { color: "#c62828" },
            },
          ],
        },
      ],
      graphic: {
        type: "text",
        left: "center",
        top: "middle",
        style: {
          text: rate == null ? "—" : `${rate}%`,
          fontSize: 16,
          fontWeight: 700,
          fill: theme.palette.text.primary,
        },
      },
    };
  }, [failure, rate, success, t, theme.palette.text.primary, total]);

  return (
    <Stack spacing={2}>
      <StatsSection title={t("resourceQueryStats.autoDisable")}>
        <AppToggleField
          hideLabel
          label={t("resourceQueryStats.autoDisable")}
          value={autoDisableEnabled ? "true" : "false"}
          onChange={(v) => onAutoDisableChange({ autoDisableEnabled: v === "true" })}
          options={[
            { value: "true", label: t("common.yes") },
            { value: "false", label: t("common.no") },
          ]}
        />
        <Typography variant="body2" color="text.secondary">
          {t("resourceQueryStats.autoDisableHelp")}
        </Typography>
        {autoDisableEnabled && (
          <AppFormGrid>
            <AppFormField size={GRID_HALF}>
              <AppTextField
                label={t("resourceQueryStats.minSuccessPercent")}
                type="number"
                value={minSuccessPercent}
                onChange={(e) => onAutoDisableChange({ minSuccessPercent: e.target.value })}
                fullWidth
                inputProps={{ min: 0, max: 100 }}
              />
            </AppFormField>
            <AppFormField size={GRID_HALF}>
              <AppTextField
                label={t("resourceQueryStats.minCalls")}
                type="number"
                value={minCalls}
                onChange={(e) => onAutoDisableChange({ minCalls: e.target.value })}
                fullWidth
                inputProps={{ min: 1 }}
              />
            </AppFormField>
          </AppFormGrid>
        )}
      </StatsSection>

      {saved && (
        <StatsSection
          title={t("resourceQueryStats.title")}
          action={
            onReset ? (
              <AppButton size="small" variant="outlined" onClick={onReset} disabled={resetting || total === 0}>
                {t("resourceQueryStats.reset")}
              </AppButton>
            ) : undefined
          }
        >
          {stats.autoDisabledAt && (
            <Alert severity="warning">
              {t("resourceQueryStats.autoDisabledNotice", {
                rate: rate ?? 0,
                threshold: minSuccessPercent || "—",
              })}
            </Alert>
          )}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, minmax(0, 1fr))" },
              gap: 1.25,
            }}
          >
            <MetricTile label={t("resourceQueryStats.total")} value={total} />
            <MetricTile label={t("resourceQueryStats.success")} value={success} color="success.main" />
            <MetricTile label={t("resourceQueryStats.failure")} value={failure} color="error.main" />
            <MetricTile label={t("resourceQueryStats.successRate")} value={rate == null ? "—" : `${rate}%`} />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {t("resourceQueryStats.cumulativeHint")}
          </Typography>
          {option ? (
            <Box
              sx={{
                width: 148,
                height: 148,
                mx: "auto",
                border: 1,
                borderColor: "divider",
                borderRadius: 1.5,
                bgcolor: "background.paper",
              }}
            >
              <ReactECharts option={option} style={{ height: "100%", width: "100%" }} notMerge opts={{ renderer: "canvas" }} />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("resourceQueryStats.empty")}
            </Typography>
          )}
        </StatsSection>
      )}

      {saved && (
        <StatsSection title={t("resourceQueryStats.history")}>
          <Typography variant="caption" color="text.secondary">
            {t("resourceQueryStats.historyHint", {
              window: QUERY_HISTORY_WINDOW,
              size: historyPageSize,
            })}
          </Typography>
          {historyLoading ? (
            <Stack alignItems="center" py={2}>
              <CircularProgress size={22} />
            </Stack>
          ) : history.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("resourceQueryStats.historyEmpty")}
            </Typography>
          ) : (
            <TableContainer
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1.5,
                bgcolor: "background.paper",
              }}
            >
              <Table data-no-stack size="small">
                <TableHead sx={{ "& .MuiTableCell-head": { bgcolor: "action.hover", fontWeight: 700 } }}>
                  <TableRow>
                    <TableCell>{t("resourceQueryStats.executedAt")}</TableCell>
                    <TableCell>{t("resourceQueryStats.status")}</TableCell>
                    <TableCell>{t("resourceQueryStats.origin")}</TableCell>
                    <TableCell>{t("resourceQueryStats.user")}</TableCell>
                    <TableCell>{t("resourceQueryStats.duration")}</TableCell>
                    <TableCell align="center" sx={{ width: 56 }}>
                      {t("resourceQueryStats.query")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <Typography variant="caption" display="block">
                          {row.executedAt ? new Date(row.executedAt).toLocaleString() : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={row.success ? "success" : "error"}
                          variant="outlined"
                          label={row.success ? t("resourceQueryStats.succeeded") : t("resourceQueryStats.failed")}
                        />
                        {!row.success && row.error && (
                          <Typography variant="caption" color="error" display="block" sx={{ mt: 0.25, maxWidth: 220 }} noWrap title={row.error}>
                            {row.error}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{row.origin || "—"}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{row.username || "—"}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {row.durationMs == null ? "—" : `${row.durationMs} ms`}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ width: 56 }}>
                        <AppIconButton
                          size="small"
                          title={t("resourceQueryStats.viewQuery")}
                          aria-label={t("resourceQueryStats.viewQuery")}
                          disabled={!row.query}
                          onClick={() => setQueryView(row.query || "")}
                        >
                          <VisibilityOutlinedIcon fontSize="small" />
                        </AppIconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {onHistoryPageChange && (historyTotal ?? 0) > 0 && (
                <TablePagination
                  component="div"
                  count={historyTotal ?? history.length}
                  page={historyPage}
                  onPageChange={(_, next) => onHistoryPageChange(next)}
                  rowsPerPage={historyPageSize}
                  rowsPerPageOptions={[historyPageSize]}
                  labelRowsPerPage=""
                  sx={{
                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-input": { display: "none" },
                  }}
                />
              )}
            </TableContainer>
          )}
          <AppDialog open={queryView != null} onClose={() => setQueryView(null)} maxWidth="md" fullWidth>
            <AppDialogTitle>{t("resourceQueryStats.query")}</AppDialogTitle>
            <AppDialogContent>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                  bgcolor: "action.hover",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontFamily: "monospace",
                  fontSize: "0.8125rem",
                  lineHeight: 1.5,
                }}
              >
                {queryView}
              </Box>
            </AppDialogContent>
            <AppDialogActions>
              <AppDialogCancelButton onClick={() => setQueryView(null)}>{t("common.close")}</AppDialogCancelButton>
            </AppDialogActions>
          </AppDialog>
        </StatsSection>
      )}
    </Stack>
  );
}

function StatsSection({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        p: 2,
        bgcolor: "background.paper",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1.5, pb: 1, borderBottom: 1, borderColor: "divider" }}
      >
        <Typography variant="subtitle2" component="h3">
          {title}
        </Typography>
        {action}
      </Stack>
      <Stack spacing={1.5}>{children}</Stack>
    </Box>
  );
}

function MetricTile({
  label,
  value,
  color,
}: {
  label: string;
  value: string | number;
  color?: string;
}) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1.5,
        px: 1.5,
        py: 1.25,
        minWidth: 0,
        bgcolor: "action.hover",
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.3 }}>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} color={color} sx={{ mt: 0.5, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  );
}
